import { useEffect } from "react";
import { Link } from "react-router-dom";

import { Alert } from "@/components/ui/Alert";
import { Skeleton } from "@/components/ui/Skeleton";
import { ApiError } from "@/lib/api";

import { DeliveryAddressInput } from "../service_area/DeliveryAddressInput";
import { hashAddress, track } from "../lib/analytics";
import { EquipmentSilhouette } from "./EquipmentSilhouette";
import { HeroEquipmentArt } from "./HeroEquipmentArt";
import { usePublicCategories } from "./api";
import type { CategorySummary } from "./types";

// Phase 4e brand-visual-rollout (take 2) — top-of-funnel home page
// redesigned for the red/white/blue identity. Sections:
//
//   1. Hero — flag-navy → brand-black gradient, italic display
//      heading, accent badge, primary brand-red CTA + secondary
//      outline CTA, inline service-area check on a brand-white card.
//   2. Trust strip — three navy callouts with red bullets.
//   3. Categories — section heading with brand-red underline accent;
//      cards lift on hover with a brand-red top-border.
//   4. Why JDR — four-up grid with brand-red feature bullets.
//   5. Pre-footer CTA band — full-bleed flag-navy with red CTA.
//
// Animations are subtle and Tailwind-only (no Framer Motion):
// fade-in-up on the hero copy, hover lift on category cards, a
// gentle red ring pulse on the primary CTA at first render.
export function HomePage(): JSX.Element {
  useEffect(() => {
    track({ name: "home_viewed" });
  }, []);

  const { data, isPending, isError, error } = usePublicCategories();

  return (
    <div className="space-y-12 pb-12 md:space-y-16">
      <HeroSection />
      <TrustStrip />

      {/* Phase 4r polish — alternate section backgrounds. The two
          non-navy sections used to blur together on the parent
          `<main>`'s zinc-50; promoting each to its own full-bleed
          band (white → zinc-50) gives a visible cadence between
          the navy strips above and below. */}
      <section
        aria-labelledby="categories-heading"
        className={`${FULL_BLEED} bg-brand-white`}
      >
        <div className="mx-auto max-w-6xl space-y-6 px-4 py-12 md:px-6 md:py-16">
          <SectionHeading
            id="categories-heading"
            eyebrow="Catalog"
            lede="Skid steers, mini-excavators, lifts, generators — everything we keep maintained, fueled, and jobsite-ready."
          >
            Browse equipment
          </SectionHeading>

          {isError ? (
            <Alert variant="destructive">
              We couldn&rsquo;t load categories.
              {error instanceof ApiError ? ` (HTTP ${error.status})` : null}
            </Alert>
          ) : null}

          {isPending ? <CategoryGridSkeleton /> : null}

          {data && data.length > 0 ? <CategoryGrid categories={data} /> : null}

          {data && data.length === 0 && !isError ? (
            <p className="text-sm text-zinc-600">No categories yet. Check back soon.</p>
          ) : null}
        </div>
      </section>

      <WhyJdrSection />
      <PreFooterCta />
    </div>
  );
}

// The CustomerApp's <main> caps at max-w-6xl with side padding; the
// hero / trust strip / pre-footer CTA break out of that with the
// classic CSS "full-bleed inside a constrained parent" pattern:
// `relative left-1/2 right-1/2 -mx-[50vw] w-screen`. The combination
// pushes the section to viewport width without disturbing siblings.
const FULL_BLEED = "relative left-1/2 right-1/2 -mx-[50vw] w-screen";

function HeroSection(): JSX.Element {
  return (
    <section
      aria-labelledby="hero-heading"
      className={`${FULL_BLEED} isolate overflow-hidden bg-hero-flag text-brand-white`}
    >
      {/* Full-bleed hero photo (decorative, navy-masked for legibility).
          Rendered first so the accent line + content layer over it. */}
      <HeroEquipmentArt />
      {/* Hairline brand-red top accent — reads as the underline of the
          JDR logo's "Equipment Rentals" banner without repeating the
          flag fill anywhere outside the logo asset. */}
      <div
        aria-hidden="true"
        className="absolute inset-x-0 top-0 z-10 h-1 bg-brand-red"
      />

      <div className="relative mx-auto max-w-6xl px-4 py-16 md:px-6 md:py-24 lg:py-28">
        <div className="grid gap-10 lg:grid-cols-[1.1fr_1fr] lg:items-center">
          <div>
            <span
              data-brand-accent="flag-navy"
              className="inline-flex items-center gap-2 rounded-full border border-brand-white/30 bg-brand-white/10 px-3 py-1 text-xs font-semibold uppercase tracking-[0.18em] text-brand-white backdrop-blur-sm motion-safe:animate-fade-in"
            >
              <span aria-hidden="true" className="inline-block h-1.5 w-1.5 rounded-full bg-brand-red" />
              Family-owned · Colorado-based
            </span>
            <h1
              id="hero-heading"
              className="mt-5 max-w-[14ch] font-serif text-4xl font-bold italic leading-[1.05] tracking-tight text-brand-white sm:text-5xl md:text-6xl motion-safe:animate-fade-in-up"
            >
              {/* Phase 4r polish — `whitespace-nowrap` keeps the hyphenated
                  compound together so a narrow viewport can't break it
                  as `contractor- / grade`. The `max-w-[14ch]` cap then
                  controls the line break to land between the words. */}
              <span className="whitespace-nowrap">Rent contractor-grade</span>{" "}
              <span className="text-brand-red">equipment.</span>
            </h1>
            <p className="mt-5 max-w-xl text-base text-zinc-200 sm:text-lg motion-safe:animate-fade-in-up-delay">
              Delivered to your jobsite in Colorado. Family-owned since day one,
              fueled and inspected before it leaves the lot.
            </p>

            <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:items-center motion-safe:animate-fade-in-up-slow">
              <Link
                to="/search"
                className="inline-flex items-center justify-center rounded-md bg-brand-red px-6 py-3 text-sm font-semibold uppercase tracking-wide text-brand-white shadow-lg shadow-brand-red/30 transition hover:bg-brand-red-dark focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-white focus-visible:ring-offset-2 focus-visible:ring-offset-flag-navy motion-safe:animate-pulse-red"
              >
                Browse equipment
                <svg
                  aria-hidden="true"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                  className="ml-2 h-4 w-4"
                >
                  <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M3 10a.75.75 0 0 1 .75-.75h10.69L11.22 6.03a.75.75 0 1 1 1.06-1.06l4.5 4.5a.75.75 0 0 1 0 1.06l-4.5 4.5a.75.75 0 0 1-1.06-1.06l3.22-3.22H3.75A.75.75 0 0 1 3 10Z"
                  />
                </svg>
              </Link>
              <a
                href="tel:+13034354328"
                className="inline-flex items-center justify-center rounded-md border-2 border-brand-white/70 px-6 py-3 text-sm font-semibold uppercase tracking-wide text-brand-white transition hover:border-brand-white hover:bg-brand-white/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-white focus-visible:ring-offset-2 focus-visible:ring-offset-flag-navy"
              >
                <svg
                  aria-hidden="true"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                  className="mr-2 h-4 w-4"
                >
                  <path d="M2 3.5A1.5 1.5 0 0 1 3.5 2h1.879a1.5 1.5 0 0 1 1.422 1.025l.83 2.49a1.5 1.5 0 0 1-.36 1.524L6.21 8.21a11.04 11.04 0 0 0 5.58 5.58l1.171-1.062a1.5 1.5 0 0 1 1.524-.36l2.49.83A1.5 1.5 0 0 1 18 14.62V16.5a1.5 1.5 0 0 1-1.5 1.5h-1A13.5 13.5 0 0 1 2 4.5v-1Z" />
                </svg>
                Call 303-435-4328
              </a>
            </div>
          </div>

          <div className="rounded-xl bg-brand-white p-5 text-brand-black shadow-2xl shadow-flag-navy-dark/40 ring-1 ring-brand-black/5 motion-safe:animate-fade-in-up-delay md:p-6">
            <p className="text-xs font-semibold uppercase tracking-[0.15em] text-flag-navy">
              Check your jobsite
            </p>
            <p className="mt-1 text-sm text-zinc-600">
              We deliver across the Front Range. Enter your address — we&rsquo;ll
              confirm coverage instantly.
            </p>
            <div className="mt-4">
              <DeliveryAddressInput
                label="Delivery address"
                onAddressChange={(addr) => {
                  if (addr.trim().length > 0) {
                    track({ name: "service_area_checked", address_hash: hashAddress(addr) });
                  }
                }}
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function TrustStrip(): JSX.Element {
  const items = [
    { label: "Family-owned", body: "Independent. Owner-operated since day one." },
    { label: "Same-day quotes", body: "Talk to a real person, not a call center." },
    { label: "Insured fleet", body: "Maintained, inspected, jobsite-ready." }
  ];
  return (
    <section
      aria-label="Why customers choose JDR"
      className={`${FULL_BLEED} bg-flag-navy text-brand-white`}
    >
      <div className="mx-auto grid max-w-6xl gap-px bg-flag-navy/40 sm:grid-cols-3">
        {items.map((item) => (
          <div key={item.label} className="bg-flag-navy px-5 py-6 md:px-8">
            <p className="flex items-center gap-3 text-sm font-semibold uppercase tracking-[0.18em]">
              <span aria-hidden="true" className="inline-block h-1.5 w-6 rounded-full bg-brand-red" />
              {item.label}
            </p>
            <p className="mt-2 text-sm text-brand-white/80">{item.body}</p>
          </div>
        ))}
      </div>
    </section>
  );
}

function WhyJdrSection(): JSX.Element {
  const items = [
    {
      title: "American family-owned",
      body: "Independent. Owner-operated, direct line, no call centers."
    },
    {
      title: "Colorado-based",
      body: "Same-day quotes, next-day delivery across our service area."
    },
    {
      title: "Insured contractors only",
      body: "We verify your Certificate of Insurance before dispatch."
    },
    {
      title: "Contractor-grade, jobsite-ready",
      body: "Fueled, greased, and inspected before it goes out the door."
    }
  ];
  return (
    <section
      aria-labelledby="why-heading"
      className={`${FULL_BLEED} bg-zinc-50`}
    >
      <div className="mx-auto max-w-6xl space-y-6 px-4 py-12 md:px-6 md:py-16">
        <SectionHeading id="why-heading" eyebrow="Why JDR">
          Built by people who use it.
        </SectionHeading>
        <ul className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {items.map((item) => (
            <li
              key={item.title}
              className="group relative overflow-hidden rounded-lg border border-zinc-200 bg-brand-white p-5 transition hover:-translate-y-0.5 hover:border-brand-red/40 hover:shadow-lg"
            >
              <span
                aria-hidden="true"
                className="absolute inset-x-0 top-0 h-0.5 bg-brand-red opacity-0 transition group-hover:opacity-100"
              />
              <p className="text-base font-semibold text-brand-black">{item.title}</p>
              <p className="mt-2 text-sm text-zinc-600">{item.body}</p>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}

function PreFooterCta(): JSX.Element {
  return (
    <section aria-label="Get a quote" className={`${FULL_BLEED} isolate bg-hero-flag text-brand-white`}>
      <div className="absolute inset-x-0 top-0 h-1 bg-brand-red" aria-hidden="true" />
      <div className="mx-auto flex max-w-6xl flex-col gap-6 px-4 py-12 md:flex-row md:items-center md:justify-between md:px-6 md:py-16">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-brand-white/70">
            Ready when you are
          </p>
          <h2 className="mt-2 max-w-2xl font-serif text-2xl font-bold italic md:text-3xl">
            Break ground sooner. Call us or browse the fleet.
          </h2>
        </div>
        <div className="flex flex-col gap-3 sm:flex-row">
          <Link
            to="/search"
            className="inline-flex items-center justify-center rounded-md bg-brand-red px-6 py-3 text-sm font-semibold uppercase tracking-wide text-brand-white shadow-lg shadow-brand-red/40 transition hover:bg-brand-red-dark focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-white focus-visible:ring-offset-2 focus-visible:ring-offset-flag-navy"
          >
            Browse equipment
          </Link>
          <a
            href="tel:+13034354328"
            className="inline-flex items-center justify-center rounded-md border-2 border-brand-white/70 px-6 py-3 text-sm font-semibold uppercase tracking-wide text-brand-white transition hover:border-brand-white hover:bg-brand-white/10"
          >
            303-435-4328
          </a>
        </div>
      </div>
    </section>
  );
}

function SectionHeading({
  id,
  eyebrow,
  lede,
  children
}: {
  id: string;
  eyebrow: string;
  // Phase 4r polish — optional 1-sentence lede paragraph below the
  // brand-red underline. The audit (#5) flagged the categories
  // heading as visually under-weight against the WhyJDR heading;
  // a single lede sentence anchors the section without ballooning.
  lede?: string;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <div className="flex flex-col gap-1.5">
      <p className="text-xs font-semibold uppercase tracking-[0.2em] text-brand-red">{eyebrow}</p>
      <h2
        id={id}
        className="font-serif text-2xl font-bold italic tracking-tight text-brand-black md:text-3xl"
      >
        {children}
      </h2>
      <span aria-hidden="true" className="mt-2 inline-block h-0.5 w-12 bg-brand-red" />
      {lede ? (
        <p className="mt-3 max-w-2xl text-sm text-zinc-600 md:text-base">{lede}</p>
      ) : null}
    </div>
  );
}

function CategoryGrid({ categories }: { categories: CategorySummary[] }): JSX.Element {
  return (
    <ul className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
      {categories.map((c) => (
        <li key={c.id}>
          <Link
            to={`/c/${c.slug}`}
            className="group block h-full overflow-hidden rounded-lg border border-zinc-200 bg-brand-white transition hover:-translate-y-0.5 hover:border-brand-red/40 hover:shadow-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-red focus-visible:ring-offset-2"
          >
            <div className="relative aspect-[4/3] w-full overflow-hidden bg-zinc-100">
              {c.hero_image_url ? (
                <img
                  src={c.hero_image_url}
                  alt=""
                  loading="lazy"
                  decoding="async"
                  className="h-full w-full object-cover transition duration-500 group-hover:scale-[1.03]"
                />
              ) : (
                <EquipmentSilhouette categorySlug={c.slug} />
              )}
              {/* Hover-only brand-red accent strip along the bottom of
                  the image — same vocabulary as the WhyJdr cards. */}
              <span
                aria-hidden="true"
                className="absolute inset-x-0 bottom-0 h-0.5 bg-brand-red opacity-0 transition group-hover:opacity-100"
              />
            </div>
            <div className="p-4">
              <p className="text-base font-semibold text-brand-black">{c.name}</p>
              {/* Phase 4e CO-only storefront FR-20: count carries the
                  meaning, no jargon noun. */}
              <p className="mt-1 text-sm text-zinc-600">{c.type_count} listed</p>
            </div>
          </Link>
        </li>
      ))}
    </ul>
  );
}

function CategoryGridSkeleton(): JSX.Element {
  return (
    <ul className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4" aria-busy="true">
      {Array.from({ length: 4 }).map((_, i) => (
        <li key={i}>
          <Skeleton className="h-64" />
        </li>
      ))}
    </ul>
  );
}
